// client/src/dnd/emitter_tool/engine/engine_parts/gravity/boundary.js
//
// Construct a boundary from the emitter shape (or the frame for "random").
// Boundary object has the form:
//   {
//     type: "circle" | "square" | "polygon" | "frame",
//     origin: {x,y},         // logical centre for gravity rays
//     // type-specific:
//     radiusPx,               // for circle
//     width, height, rotRad,  // for square (rotated)
//     points: [{x,y}, ...],   // for polygon (closed or implicit closed)
//     rect: {x,y,width,height} // for frame
//   }

function isFiniteNum(v) { return Number.isFinite(v); }
const DEG = Math.PI / 180;

export function buildBoundary({ emitterShape, shapeSettings = {}, origin = { x: 0, y: 0 }, pxPerMeter = 80, imageRect = { x: 0, y: 0, width: 0, height: 0 } }) {
  const shape = String(emitterShape || "dot").toLowerCase();

  // "random" uses the frame rectangle in local coordinates
  if (shape === "random") {
    const rect = {
      x: imageRect?.x || 0,
      y: imageRect?.y || 0,
      width: Math.max(0, imageRect?.width || 0),
      height: Math.max(0, imageRect?.height || 0),
    };
    return {
      type: "frame",
      origin: { x: rect.x + rect.width * 0.5, y: rect.y + rect.height * 0.5 },
      rect,
    };
  }

  if (shape === "circle") {
    // Expect either metres or pixels; prefer explicit pixel radius if present.
    let rPx = Number(shapeSettings?.radiusPx);
    if (!isFiniteNum(rPx) || rPx <= 0) {
      const rM = Number(shapeSettings?.radiusM);
      rPx = isFiniteNum(rM) && rM > 0 ? rM * pxPerMeter : 50;
    }
    return { type: "circle", origin: { x: origin.x, y: origin.y }, radiusPx: Math.max(1e-3, rPx) };
  }

  if (shape === "square" || shape === "rect") {
    let wPx = Number(shapeSettings?.widthPx);
    let hPx = Number(shapeSettings?.heightPx);
    if (!isFiniteNum(wPx) || wPx <= 0) {
      const wM = Number(shapeSettings?.widthM);
      wPx = isFiniteNum(wM) && wM > 0 ? wM * pxPerMeter : 100;
    }
    if (!isFiniteNum(hPx) || hPx <= 0) {
      const hM = Number(shapeSettings?.heightM);
      hPx = isFiniteNum(hM) && hM > 0 ? hM * pxPerMeter : 100;
    }
    const rotDeg = Number(shapeSettings?.rotationDeg || 0);
    return {
      type: "square",
      origin: { x: origin.x, y: origin.y },
      width: Math.max(1e-3, wPx),
      height: Math.max(1e-3, hPx),
      rotRad: (rotDeg || 0) * DEG,
    };
  }

  if (shape === "polygon" || shape === "triangle" || Array.isArray(shapeSettings?.points)) {
    // Expect points in local/image space
    let pts = shapeSettings?.points;
    if (!Array.isArray(pts) || pts.length < 3) {
      // fallback simple triangle around origin
      pts = [
        { x: origin.x + 0, y: origin.y - 60 },
        { x: origin.x - 50, y: origin.y + 40 },
        { x: origin.x + 50, y: origin.y + 40 },
      ];
    }
    return {
      type: "polygon",
      origin: { x: origin.x, y: origin.y },
      points: pts.map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 })),
    };
  }

  // default: tiny circle at origin (acts like a point)
  return { type: "circle", origin: { x: origin.x, y: origin.y }, radiusPx: 1 };
}

export function centreForBoundary(boundary) {
  if (!boundary) return { x: 0, y: 0 };
  if (boundary.type === "frame") {
    const r = boundary.rect || { x: 0, y: 0, width: 0, height: 0 };
    return { x: r.x + r.width * 0.5, y: r.y + r.height * 0.5 };
  }
  return { x: boundary.origin?.x || 0, y: boundary.origin?.y || 0 };
}
